import React from 'react';

/**
 * EGGS Container Component
 * Based on EGGS Design System specifications
 * 
 * @param {string} size - sm | md | lg | xl | full
 * @param {boolean} centered - Center the container
 * @param {string} padding - none | sm | md | lg | xl
 * @param {React.ReactNode} children - Container content
 * @param {string} className - Additional CSS classes
 * @param {object} props - Additional container props
 */
export default function Container({
  size = 'lg',
  centered = true,
  padding = 'md',
  children,
  className = '',
  ...props
}) {
  // Base classes for all containers
  const baseClasses = [
    'w-full',
    centered ? 'mx-auto' : ''
  ].join(' ');

  // Size-specific max-width classes
  const sizeClasses = {
    sm: 'max-w-sm',      // 384px
    md: 'max-w-md',      // 448px
    lg: 'max-w-4xl',     // 896px - Good for main content
    xl: 'max-w-6xl',     // 1152px - Wide layouts
    full: 'max-w-none'   // No max width
  };

  // Padding classes
  const paddingClasses = {
    none: '',
    sm: 'px-4 py-2',
    md: 'px-6 py-4',
    lg: 'px-8 py-6',
    xl: 'px-12 py-8'
  };

  return (
    <div
      className={`${baseClasses} ${sizeClasses[size]} ${paddingClasses[padding]} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

// Export convenience components for common container sizes
export const SmallContainer = (props) => (
  <Container size="sm" {...props} />
);

export const MediumContainer = (props) => (
  <Container size="md" {...props} />
);

export const LargeContainer = (props) => (
  <Container size="lg" {...props} />
);

export const ExtraLargeContainer = (props) => (
  <Container size="xl" {...props} />
);

export const FullContainer = (props) => (
  <Container size="full" {...props} />
);